<template>
    <w-text-field
        :value="value"
        :allow-autocomplete="allowAutocomplete"
        :append-icon="showPassword ? 'visibility_off' : 'visibility'"
        :autofocus="autofocus"
        :counter="counter"
        :disabled="disabled"
        :error-messages="errorMessages"
        :hint="hint"
        :icon="icon"
        :id="id"
        :label="label"
        :loading="loading"
        :type="showPassword ? 'text' : 'password'"
        :maxLength="maxLength"
        :persistent-hint="persistentHint"
        :placeholder="placeholder"
        :rules="fieldRulesFormatted"
        :success-messages="successMessages"
        :validate-on-blur="validateOnBlur"
        @click:append="showPassword = !showPassword"
        @autofill="handleAutofill"
        @focus="handleFocus"
        @input="handleInput"
        @enter="handleEnter"
    ></w-text-field>
</template>

<script>
    import WFieldChecksMixin from '../../mixins/forms/w-field-checks-mixin'
    import WTextField from '../WTextField'
    import _ from 'underscore'

    /**
     * A password form field.
     */
    export default {
        name: 'WPasswordField',
        mixins: [WFieldChecksMixin],
        components: {
            WTextField
        },
        props: {
            /**
             * Focus the field (so user can enter text straight away).
             */
            autofocus: {
                type: Boolean,
                default: false
            },
            /**
             * Colour of the field.
             */
            color: {
                type: String,
                default: 'primary'
            },
            /**
             * Count the characters/number of selections.
             */
            counter: {
                type: [Boolean, Number],
                default: true
            },
            /**
             * Disable input in to the field.
             */
            disabled: {
                type: Boolean,
                default: false
            },
            /**
             * Display a hint under the field when entering text.
             */
            hint: {
                type: String,
                default: ''
            },
            /**
             * Icon to prepend the field. Can be either 'lock' or 'vpn_key'.
             */
            icon: {
                type: String,
                default: 'lock',
                validator: (icon) => _.contains(['lock', 'vpn_key'], icon)
            },
            /**
             * Label to show for the field.
             */
            label: {
                type: String,
                default: 'Password'
            },
            /**
             * Show a loading bar at the bottom of the field entry box.
             */
            loading: {
                type: Boolean,
                default: false
            },
            /**
             * Apply a hard character limit to the field.
             */
            maxLength: {
                type: [Number, Boolean],
                default: false
            },
            /**
             * Make the hint persistent (shows even when the field is not focused).
             */
            persistentHint: {
                type: Boolean,
                default: false
            },
            /**
             * Placeholder value
             */
            placeholder: {
                type: String,
                default: ''
            },
            /**
             * Display success message(s) and style the field green.
             */
            successMessages: {
                type: [String, Array],
                default: () => []
            },
            /**
             * Validate the field on blue (instead of on input).
             */
            validateOnBlur: {
                type: Boolean,
                default: false
            },
            /**
             * The value of the field.
             */
            value: {
                type: String,
                default: ''
            }
        },
        data: () => ({
            showPassword: false
        }),
        computed: {
            passwordFieldRules () {
                return _.values(_.pick(this.validationRules.password, [
                    'minLength',
                    'maxLength',
                    'mustContainLetter',
                    'mustContainNumber'
                ]))
            },
            fieldRulesFormatted () {
                const vm = this

                if (!_.isEmpty(vm.customRules)) {
                    return vm.customRules
                }

                return [
                    ...vm.fieldRules,
                    ...vm.passwordFieldRules
                ]
            }
        },
        methods: {
            handleAutofill (val) {
                /**
                 * Focus event.
                 * @type {boolean}
                 */
                this.$emit('autofill', val)
            },
            handleFocus (val) {
                /**
                 * Focus event.
                 * @type {string}
                 */
                this.$emit('focus', val)
            },
            handleInput (val) {
                /**
                 * Input event.
                 * @type {string}
                 */
                this.$emit('input', val)
            }
        }
    }
</script>
